import { baseApi } from '@krp/client-runtime'

import { applyKind } from '../model/optimistic'
import type {
  AllAssignmentsRange,
  Assignment,
  CashierDayArgs,
  CashierDaySetArgs,
  CreatePlaceArgs,
  Member,
  MemberArgs,
  MemberRemoveArgs,
  MembersArgs,
  Place,
  PlacesArgs,
  ScheduleAccess,
  UpdatePlaceArgs,
} from '../model/types'
import { decodeAccess, decodeAssignments, decodeMembers, decodePlace, decodePlaces } from './contracts'
import { SCHEDULE_URLS } from './urls'

/**
 * Служба графика в общем `baseApi`. Данные сервера живут только в этом кэше; в слайсе — намерение человека. Команды не
 * повторяются transport-ом. Действие над клеткой — точечная команда (кассир в день): кэш назначений правится сразу, до ответа, а при
 * отказе правка откатывается. Состав объекта хранит сервер: добавление и снятие кассира — команды над ним.
 * Назначения и состав перечитываются после любой команды — и после неудачной: обрыв ответа оставляет исход неизвестным, а верно
 * показывает только чтение; кроме того, постановка на день сама вносит кассира в состав объекта на сервере.
 */
export const SCHEDULE_TAG = 'Schedule' as const

const ACCESS = { type: SCHEDULE_TAG, id: 'ACCESS' } as const
const PLACES = { type: SCHEDULE_TAG, id: 'PLACES' } as const
const ASSIGNMENTS = { type: SCHEDULE_TAG, id: 'ASSIGNMENTS' } as const
const MEMBERS = { type: SCHEDULE_TAG, id: 'MEMBERS' } as const

export const scheduleApi = baseApi.enhanceEndpoints({ addTagTypes: [SCHEDULE_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    scheduleAccess: build.query<ScheduleAccess, void>({
      query: () => ({ url: SCHEDULE_URLS.access, decode: decodeAccess }),
      providesTags: [ACCESS],
    }),
    schedulePlaces: build.query<Place[], PlacesArgs>({
      query: ({ includeArchived }) => ({ url: SCHEDULE_URLS.places, params: { includeArchived }, decode: decodePlaces }),
      providesTags: [PLACES],
    }),
    schedulePlaceCreate: build.mutation<Place, CreatePlaceArgs>({
      query: ({ name }) => ({ url: SCHEDULE_URLS.places, method: 'POST', body: { name }, decode: decodePlace }),
      invalidatesTags: [PLACES],
    }),
    schedulePlaceUpdate: build.mutation<Place, UpdatePlaceArgs>({
      query: ({ id, name, archived }) => ({ url: SCHEDULE_URLS.place(id), method: 'PUT', body: { name, archived }, decode: decodePlace }),
      invalidatesTags: [PLACES],
    }),
    /** Назначения всех объектов за период: из них же таблица объекта, строки «По кассирам» и пометки «занято на другом объекте». */
    scheduleAssignmentsAll: build.query<Assignment[], AllAssignmentsRange>({
      query: ({ from, to }) => ({ url: SCHEDULE_URLS.allAssignments, params: { from, to }, decode: decodeAssignments }),
      providesTags: [ASSIGNMENTS],
    }),
    scheduleMembers: build.query<Member[], MembersArgs>({
      query: ({ placeId }) => ({ url: SCHEDULE_URLS.members(placeId), decode: decodeMembers }),
      providesTags: [MEMBERS],
    }),
    scheduleMemberAdd: build.mutation<unknown, MemberArgs>({
      query: ({ placeId, cashierId }) => ({ url: SCHEDULE_URLS.member(placeId, cashierId), method: 'PUT' }),
      invalidatesTags: [MEMBERS],
    }),
    /** Снять кассира с объекта: служба сама удаляет его выходы с `clearFrom` включительно и запись состава одним сохранением. */
    scheduleMemberRemove: build.mutation<unknown, MemberRemoveArgs>({
      query: ({ placeId, cashierId, clearFrom }) => ({ url: SCHEDULE_URLS.member(placeId, cashierId), method: 'DELETE', params: { clearFrom } }),
      invalidatesTags: [ASSIGNMENTS, MEMBERS],
    }),
    /** Поставить отметку клетки («Р», «В» или «Н»); запись того же дня меняет вид, а не дублируется. */
    scheduleCashierDaySet: build.mutation<unknown, CashierDaySetArgs>({
      query: ({ placeId, date, cashierId, kind }) => ({ url: SCHEDULE_URLS.cashierDay(placeId, date, cashierId), method: 'PUT', body: { kind: kind ?? 'work' } }),
      invalidatesTags: [ASSIGNMENTS, MEMBERS],
      onQueryStarted: async ({ placeId, date, cashierId, kind }, { dispatch, getState, queryFulfilled }): Promise<void> => {
        const patches = scheduleApi.util
          .selectCachedArgsForQuery(getState(), 'scheduleAssignmentsAll')
          .filter((range) => date >= range.from && date <= range.to)
          .map((range) => dispatch(scheduleApi.util.updateQueryData('scheduleAssignmentsAll', range, (draft) => applyKind(draft, placeId, cashierId, date, kind ?? 'work'))))
        try {
          await queryFulfilled
        } catch {
          for (const patch of patches) patch.undo()
        }
      },
    }),
    /** Очистить клетку: запись любого вида удаляется. */
    scheduleCashierDayClear: build.mutation<unknown, CashierDayArgs>({
      query: ({ placeId, date, cashierId }) => ({ url: SCHEDULE_URLS.cashierDay(placeId, date, cashierId), method: 'DELETE' }),
      invalidatesTags: [ASSIGNMENTS, MEMBERS],
      onQueryStarted: async ({ placeId, date, cashierId }, { dispatch, getState, queryFulfilled }): Promise<void> => {
        const patches = scheduleApi.util
          .selectCachedArgsForQuery(getState(), 'scheduleAssignmentsAll')
          .filter((range) => date >= range.from && date <= range.to)
          .map((range) => dispatch(scheduleApi.util.updateQueryData('scheduleAssignmentsAll', range, (draft) => applyKind(draft, placeId, cashierId, date, null))))
        try {
          await queryFulfilled
        } catch {
          for (const patch of patches) patch.undo()
        }
      },
    }),
  }),
})

export const {
  scheduleAccess,
  schedulePlaces,
  schedulePlaceCreate,
  schedulePlaceUpdate,
  scheduleAssignmentsAll,
  scheduleMembers,
  scheduleMemberAdd,
  scheduleMemberRemove,
  scheduleCashierDaySet,
  scheduleCashierDayClear,
} = scheduleApi.endpoints
